import { useLanguage } from '../hooks/use-language';
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Globe, Loader2, Check } from "lucide-react";
import { Badge } from './ui/badge';

// English only - removed Spanish language support
const languageFlags: Record<string, string> = {
  en: "🇺🇸", // US flag for English only
};

export function LanguageSwitcher({ variant = "ghost", showText = true, size = "sm" }: { 
  variant?: "ghost" | "outline" | "secondary" | "default"; 
  showText?: boolean;
  size?: "sm" | "default" | "lg" | "icon";
}) {
  const { language, changeLanguage, supportedLanguages, isLoading, t } = useLanguage();

  // Get the current language name to display
  const currentLanguageName = supportedLanguages.find(lang => lang.code === language)?.name || '';
  const currentFlag = languageFlags[language] || '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size === "icon" ? "icon" : size}
          className={`flex items-center gap-2 rounded-full hover:bg-primary/10 ${
            size === "icon" ? "remodra-topnav-icon h-9 w-9 sm:h-9 sm:w-9 md:h-10 md:w-10" : ""
          }`}
          aria-label="Change language"
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              {size === "icon" ? (
                <Globe className="h-4 w-4 sm:h-4 sm:w-4 md:h-5 md:w-5" />
              ) : (
                <>
                  <span className="text-lg">{currentFlag}</span>
                  <Globe className="h-4 w-4" />
                </>
              )}
            </>
          )}
          {showText && size !== "icon" && (
            <span className="hidden md:inline-block">
              {currentLanguageName}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
        <DropdownMenuLabel className="text-slate-900 dark:text-slate-200">{t('settings.language')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`flex items-center justify-between text-slate-900 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ${language === lang.code ? "font-medium" : ""}`}
            disabled={isLoading}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{languageFlags[lang.code] || ''}</span>
              <span>{lang.name}</span>
            </div>
            {language === lang.code && (
              isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin ml-2" />
              ) : (
                <Check className="h-4 w-4 text-primary ml-2" />
              )
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}